import { Component, forwardRef, input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { proccessWriteValue, processDateChange } from './utils';

/**
 * DatePickerComponent - Handles date input with automatic format conversion
 *
 * This component acts as a bridge between:
 * - Backend format: MM/dd/yyyy (e.g., "10/30/2025")
 * - HTML5 input format: yyyy-MM-dd (e.g., "2025-10-30")
 *
 * The component internally manages the conversion so that:
 * - When receiving data (writeValue), it converts from backend format to input format
 * - When sending data (onChange), it converts from input format to backend format
 */
@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [FontAwesomeModule],
  templateUrl: './date-picker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor {
  label = input<string>('');
  max = input<string>();
  min = input<string>();

  // Internal value stored in HTML5 input format (yyyy-MM-dd)
  _inputValue = '';
  isDisabled = false;

  // External value in backend format (MM/dd/yyyy)
  private _backendValue = '';

  get inputValue() {
    return this._inputValue;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};

  /**
   * Called by Angular forms when the value is set from the form
   * Expects backend format (MM/dd/yyyy) and converts to input format (yyyy-MM-dd)
   */
  writeValue(value: string): void {
    const result = proccessWriteValue(value);
    this._inputValue = result.inputValue;
    this._backendValue = result.backendValue;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Called when the user changes the date in the input
   * Receives input format (yyyy-MM-dd) and converts to backend format (MM/dd/yyyy)
   */
  onDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const result = processDateChange(input.value);

    // Update input element if value was modified (e.g., year > 9999)
    if (input.value !== result.inputValue) {
      input.value = result.inputValue;
    }

    this._inputValue = result.inputValue;
    this._backendValue = result.backendValue;

    this.onChange(result.backendValue);
    this.onTouched();
  }
}
